import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from '@remix-run/react';
import { atom, useAtom } from 'jotai';

import { useTracker } from '@archipro-website/tracker';
import type { ProfessionalInitialResult } from '~/modules/professional/hook/use-professional-initial-route-data';
import { trackEnquiryEngagement } from '~/modules/tracking/util/trackEnquiryEngagement';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import { trackProjectEnquire } from '~/modules/tracking/util/trackProjectEnquiry';
import {
    ENQUIRE_AUTH_SOURCE_VALUE,
    ENQUIRE_MODAL_SHOWUP_KEY,
    ENQUIRE_MODAL_SHOWUP_KEY_VALUE as KEY_VALUE,
} from '~/modules/enquiry/constants';
import {
    AUTH_PROVIDER_PARAM_KEY,
    AUTH_SOURCE_PARAM_KEY,
} from '~/modules/root/hook/use-auth-event';
import type { UseEnquiryProps } from './use-enquiry';

interface EnquiryModalContext {
    /** The title of the modal. */
    title?: string;
    /** The professional to submit an enquiry to. */
    professional: Pick<
        ProfessionalInitialResult,
        'ID' | 'Title' | 'branchOptions' | 'CompanyLogo' | 'LogoBackgroundColor'
    >;
    /** The type of the Library Item (professional, product, project, article). */
    itemType: UseEnquiryProps['itemType'];
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: UseEnquiryProps['siteTreeID'];
    /** The source of the enquiry. Defaults to `EnquiryModal`. */
    source?: Exclude<UseEnquiryProps['source'], 'InPage'>;
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /** The CTA text that opened the modal. Used for tracking. */
    ctaLabel?: string;
    /** Include the branch dropdown if the professional has branches. Defaults to `false`. */
    includeBranch?: boolean;
    /** The project ID if the enquiry is for a project. Used for tracking. */
    projectID?: number;
}

const enquiryModalOpenAtom = atom(false);
const enquiryModalContextAtom = atom<EnquiryModalContext | null>(null);

/**
 * Hook to interact with the Enquiry Modal. Can be used to render an Enquiry
 * Modal or open it. Components opening the modal should pass in data about the
 * enquiry (professional, etc.) for the modal to render.
 * @param data Context about the enquiry for the modal to render.
 */
export function useEnquiryModal(data?: EnquiryModalContext) {
    const isAutoOpened = useRef(false);
    const isEngagementTracked = useRef(false);
    const [isOpen, setIsOpen] = useAtom(enquiryModalOpenAtom);
    const [context, setContext] = useAtom(enquiryModalContextAtom);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const tracker = useTracker();

    const openEnquiryModal = useCallback(
        ({
            title,
            ctaLabel,
            addQueryParam = false,
        }: {
            title?: string;
            ctaLabel?: string;
            addQueryParam?: boolean;
        } = {}) => {
            const newContext = data
                ? {
                      ...data,
                      ...(title && { title }),
                      ...(ctaLabel && { ctaLabel }),
                  }
                : context;
            if (!newContext) {
                return;
            }
            const { professional, itemType, siteTreeID, source } = newContext;
            if (data) {
                // Only update context if data is provided.
                setContext(newContext);
            }
            setIsOpen(true);
            isAutoOpened.current = true;

            if (addQueryParam) {
                searchParams.set(ENQUIRE_MODAL_SHOWUP_KEY, KEY_VALUE);
                setSearchParams(searchParams, { preventScrollReset: true });
            }

            trackEnquireReveal(tracker, {
                professionalName: professional.Title,
                itemID: siteTreeID,
                itemType,
                source: source ?? 'EnquiryModal',
                ctaLabel: newContext.ctaLabel,
                projectID: newContext.projectID,
                professionalID: professional.ID,
            });
            if (itemType === 'project' && newContext.relatedItem) {
                trackProjectEnquire(tracker, {
                    category: newContext.relatedItem.category,
                    step: 1,
                    project: newContext.relatedItem.title,
                    professional: professional.Title,
                });
            }
            if (!isEngagementTracked.current) {
                // Track engagement max once per page.
                trackEnquiryEngagement(tracker, {
                    professional: {
                        ID: professional.ID,
                        Title: professional.Title,
                    },
                    itemID: siteTreeID,
                    itemType,
                    source: source ?? 'EnquiryModal',
                });
                isEngagementTracked.current = true;
            }
        },
        [
            tracker,
            searchParams,
            data,
            context,
            setIsOpen,
            setContext,
            setSearchParams,
        ]
    );

    useEffect(() => {
        // Open the enquiry modal on page load if query param is present.
        if (searchParams.get(ENQUIRE_MODAL_SHOWUP_KEY) !== KEY_VALUE) {
            return;
        }
        // Redirect after social login from enquiry modal, do nothing.
        // Copied from use-enquiry-modal-by-url.tsx.
        if (
            searchParams.has(AUTH_PROVIDER_PARAM_KEY) &&
            searchParams.get(AUTH_SOURCE_PARAM_KEY) ===
                ENQUIRE_AUTH_SOURCE_VALUE
        ) {
            return;
        }
        // isAutoOpened is required to prevent infinite loop.
        if (!isAutoOpened.current && data) {
            openEnquiryModal();
        }
    }, [searchParams, data, openEnquiryModal]);

    useEffect(() => {
        isAutoOpened.current = false;
        isEngagementTracked.current = false;
    }, [location.pathname]);

    return {
        /** The open state of the enquiry modal. */
        isOpen,
        /** The current context of the enquiry modal (e.g. professional, item type, etc.). */
        context,
        /**
         * Open the enquiry modal.
         * @param title An optional title of the modal.
         * @param ctaLabel An optional CTA text that opened the modal. Used for tracking.
         * @param addQueryParam Add the query param to the URL so the modal is
         *   auto opened on page load. Defaults to `false`.
         */
        openEnquiryModal,
        /** Close the enquiry modal. */
        closeEnquiryModal: useCallback(() => {
            if (searchParams.has(ENQUIRE_MODAL_SHOWUP_KEY)) {
                searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);
                setSearchParams(searchParams, { preventScrollReset: true });
            }
            setIsOpen(false);
        }, [searchParams, setSearchParams, setIsOpen]),
    };
}
